import {
  ButtonBase,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';

import { useQuery } from '@tanstack/react-query';
import formatBytes from 'bytes';
import { formatDistanceToNowStrict } from 'date-fns';

import DownloadFile from '@/assets/icons/DownloadFile';
import { Permission } from '@/entities/permissions';
import { http } from '@/services/http';
import usePermission from '@/shared/hooks/usePermission';
import WidgetNoAccess from '@/shared/ui/feedback/WidgetNoAccess';
import EllipsisWithTooltip from '@/shared/ui/primitives/EllipsisWithTooltip';
import downloadFile from '@/utils/downloadFile';

import { EmptyState } from '@/shared/ui/feedback/EmptyState';
import Loader from '@/shared/ui/feedback/Spinner';

interface WidgetFile {
  name: string;
  size: string;
  created: string;
  fid: string;
}

interface FilesResponse {
  totalSize: number;
  items: {
    fileName: string;
    fileType: string;
    groupName: string;
    fid: string;
    latest: boolean;
    size: number;
    arrivalTime: string;
    fields?: {
      createdAt?: string;
    };
  }[];
}

type FilesWidgetProps = {
  groupName: string | undefined;
  size?: number;
  downloadable?: string;
};

const instance = http.internal;

const parseData = (data: FilesResponse): WidgetFile[] => {
  return data.items.map(item => ({
    name: item.fileName,
    size:
      formatBytes(item.size, {
        unitSeparator: ' '
      }) ?? '-',
    created: formatDistanceToNowStrict(new Date(item.fields?.createdAt ?? item.arrivalTime), {
      addSuffix: true
    }),
    fid: item.fid
  }));
};

const FilesWidgetContent = ({ groupName, size, downloadable }: FilesWidgetProps) => {
  const { hasPermission } = usePermission();
  const { data, isLoading, isError } = useQuery({
    queryKey: ['widget-files', size, groupName],
    queryFn: async () => {
      const sizeParam = size ? `&size=${size}` : '';
      const url = `/proxy/datalake/search?fileName=*&groupName=${encodeURIComponent(groupName ?? '')}${sizeParam}`;
      const response = await instance.get(url);
      return parseData(response.data);
    },
    enabled: !!groupName && hasPermission(Permission.ViewMarketScreens)
  });

  if (isLoading) return <Loader />;
  if (isError || !groupName) return <EmptyState />;
  return (
    <Paper>
      <TableContainer
        sx={{
          borderCollapse: 'separate',
          borderSpacing: 0
        }}
      >
        <Table
          stickyHeader
          size='small'
          sx={{
            width: '100%',
            tableLayout: 'fixed'
          }}
        >
          <TableHead
            sx={{
              '& th': {
                px: 3,
                pb: 1,
                borderBottom: '0.5px solid',
                borderBottomColor: 'gx.text',
                backgroundColor: 'gx.tileDark'
              }
            }}
          >
            <TableRow>
              <TableCell sx={{ width: 'auto' }}>File name</TableCell>
              <TableCell sx={{ width: 120 }}>Size</TableCell>
              <TableCell sx={{ width: 120 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data?.map(file => (
              <TableRow
                key={file.name}
                sx={{
                  height: '30px',
                  '&:nth-of-type(even)': {
                    backgroundColor: 'gx.tileLight'
                  },
                  '& td': {
                    px: 3
                  }
                }}
              >
                <TableCell
                  sx={{
                    overflow: 'hidden'
                  }}
                >
                  <EllipsisWithTooltip text={file.name} />
                </TableCell>
                <TableCell>{file.size}</TableCell>
                <TableCell>
                  <ButtonBase
                    disableRipple
                    disabled={downloadable === 'false'}
                    sx={{
                      color: 'gx.highlight'
                    }}
                    onClick={() => {
                      downloadFile(file.fid, file.name).catch(() => {
                        // downloadFile already retries and logs failures; keep this flow fire-and-forget.
                      });
                    }}
                  >
                    <DownloadFile
                      sx={{
                        mr: '4px'
                      }}
                    />
                    Download
                  </ButtonBase>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

const FilesWidget = ({ groupName, size, downloadable }: FilesWidgetProps) => {
  const { hasPermission } = usePermission();

  if (!hasPermission(Permission.ViewMarketScreens)) {
    return <WidgetNoAccess />;
  }

  return (
    <FilesWidgetContent
      groupName={groupName}
      size={size}
      downloadable={downloadable}
    />
  );
};

export default FilesWidget;
